import React, { useCallback, useEffect, useState } from "react";
import Input from "components/foundation/Input/Input";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import Modal from "components/foundation/Modal/Modal";
import { useLoginMutation } from "features/auth/hooks/mutations";
import { SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { ROUTER_PATH } from "config/path";
import { debounce } from "lodash-es";
import { saveJwt, saveUser } from "utils/utility";
import { OCTOLogo } from "assets";
import { UserLoginRequest } from "apis/data-contracts";
import { EMAIL_VALIDATION } from "config/validation";
import Toast, { useToast } from "components/foundation/Toast";
import { useRecoilState } from "recoil";
import { isLoggedOutState, isSignUpInProgressState } from "store/auth";
import { handleAppLoginSuccess } from "../../../utils/appInterface";
import AuthBottomSheetLayout from "components/auth/AuthBottomSheetLayout";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import axios from "axios";
import styled, { css } from "styled-components";

const SIGN_IN_FORM_ID = "sign-in-form";

const AuthSignInPage = () => {
  const { register, handleSubmit, formState } = useForm<UserLoginRequest>({
    mode: "onChange",
  });
  const { isValid, errors } = formState;

  /* 파라미터 */
  const [isSignUpInProgress, setIsSignUpInProgress] = useRecoilState(
    isSignUpInProgressState,
  );

  /* 모달 */
  const [errorMessage, setErrorMessage] = useState("");
  const [openSignInErrorModal, setOpenSignInErrorModal] = useState(false); // 로그인 에러
  const [openSignUpRefusalModal, setOpenSignUpRefusalModal] = useState(false); // 가입승인 거절
  const [openSignUpInProgressModal, setOpenSignUpInProgressModal] =
    useState(isSignUpInProgress); // 가입심사 진행 중

  /**
   * @description 로그인 에러 모달의 확인 버튼 클릭 이벤트를 핸들링하는 함수
   */
  const handleConfirmSignInError = () => {
    setOpenSignInErrorModal(false);
  };

  /* 로그인 폼 Submit 이벤트 */
  const loginMutation = useLoginMutation();
  const navigate = useNavigate();

  /**
   * @description 로그인 폼 제출 이벤트를 핸들링하는 함수
   * @param form 로그인 정보를 담은 폼
   */
  const onSubmit: SubmitHandler<UserLoginRequest> = (form) => {
    loginMutation.mutate(form, {
      onSuccess: async ({ data: { result } }) => {
        const { accessToken } = result || {};

        saveJwt(accessToken);
        saveUser(result);

        // 로그인 성공 시 App Inteface의 로그인 성공 메소드를 호출한다.
        handleAppLoginSuccess();

        navigate(ROUTER_PATH.home);
      },
      onError: async (error) => {
        if (axios.isAxiosError(error)) {
          const status = error.response?.data?.code;
          switch (status) {
            // 승인 대기중
            case 4000:
              setOpenSignUpInProgressModal(true);
              break;
            // 승인 거절
            case 4001:
              setOpenSignUpRefusalModal(true);
              break;
            case 404:
              const message = error.response?.data?.message;
              setErrorMessage(message);
              setOpenSignInErrorModal(true);
              break;
          }
        }
      },
    });
  };
  // onSubmit debounce 처리
  const debouncedSubmit = useCallback(
    debounce(onSubmit, 1000, { leading: true, trailing: false }),
    [],
  );

  /* 로그아웃 토스트 */
  const { open: openLogoutToast, onOpenChange: onOpenChangeLogoutToast } =
    useToast();
  const [isLoggedOut, setIsLoggedOut] = useRecoilState(isLoggedOutState);

  useEffect(() => {
    if (isLoggedOut) {
      onOpenChangeLogoutToast(true);
      setIsLoggedOut(false);
    }
  }, [isLoggedOut]);

  /* 로그인 시 리다이렉트 */
  const { data: currentUser } = useUserDetailQuery();

  useEffect(() => {
    setIsSignUpInProgress(false);
  }, []);

  if (!!currentUser) {
    // 유저가 있는 것이므로 로그인 성공 알림을 보낸다
    handleAppLoginSuccess();

    return <Navigate to={ROUTER_PATH.home} replace />;
  }

  return (
    <>
      <LogoAreaStyle>
        <p>문서관리를 위한 최적의 솔루션</p>
        <OCTOLogo />
      </LogoAreaStyle>

      <AuthBottomSheetLayout
        bottomWithKeyboard={-112}
        style={{ marginTop: "6.8rem" }}
      >
        <SignInFormStyle
          id={SIGN_IN_FORM_ID}
          onSubmit={handleSubmit(debouncedSubmit)}
        >
          <Input
            type="text"
            placeholder="이메일을 입력해주세요"
            {...register("email", {
              required: true,
              pattern: {
                value: EMAIL_VALIDATION,
                message: "이메일 형식으로 입력이 필요합니다.",
              },
            })}
            errorMessage={errors.email?.message}
          />
          <Input
            type="password"
            placeholder="비밀번호를 입력해 주세요"
            {...register("password", {
              required: true,
              minLength: {
                value: 8,
                message: "8자이상 20자 이하로 입력이 필요합니다.",
              },
              maxLength: {
                value: 20,
                message: "8자이상 20자 이하로 입력이 필요합니다.",
              },
            })}
            errorMessage={errors.password?.message}
            maxLength={20}
          />
        </SignInFormStyle>

        <SignInFormFooterStyle>
          <Button
            form={SIGN_IN_FORM_ID}
            type="submit"
            size="large"
            disabled={!isValid}
          >
            로그인
          </Button>
        </SignInFormFooterStyle>

        <AuthNavMenu className="mobile-only" />
      </AuthBottomSheetLayout>

      <AuthNavMenu className="pc-only" style={{ margin: "3.6rem" }} />

      <Modal.Root open={openSignInErrorModal}>
        <SignInModalStyle
          title="로그인 오류"
          onConfirm={handleConfirmSignInError}
        >
          {errorMessage}
        </SignInModalStyle>
      </Modal.Root>

      <Modal.Root
        open={openSignUpRefusalModal}
        onOpenChange={setOpenSignUpRefusalModal}
      >
        <SignInModalStyle title="가입 승인이 거절된 계정입니다" confirm>
          관리자에게 문의해 주세요.
        </SignInModalStyle>
      </Modal.Root>

      <Modal.Root
        open={openSignUpInProgressModal}
        onOpenChange={setOpenSignUpInProgressModal}
      >
        <SignInModalStyle
          title={"해당 계정에 대해\r\n가입 심사 진행중입니다"}
          confirm
        >
          최종 승인이 완료되면 승인 완료 이메일
          <br />
          발송됩니다.
        </SignInModalStyle>
      </Modal.Root>

      <Toast.Message
        open={openLogoutToast}
        onOpenChange={onOpenChangeLogoutToast}
      >
        로그아웃 되었습니다.
      </Toast.Message>
    </>
  );
};

export default AuthSignInPage;

const AuthNavMenu = ({ ...rest }: React.HTMLAttributes<HTMLDivElement>) => {
  const navigate = useNavigate();

  /**
   * @description 링크 버튼 클릭 이벤트 핸들러
   */
  const handleClickLinkButton = (path: string) => {
    navigate(path);
  };

  return (
    <LinkButtonGroupStyle {...rest}>
      <Button
        variant="ghost"
        size="small"
        noPadding
        onClick={() => handleClickLinkButton(ROUTER_PATH.auth.findPassword)}
      >
        비밀번호 찾기
      </Button>
      <Divider orientation="vertical" style={{ height: "1rem" }} />
      <Button
        variant="ghost"
        size="small"
        noPadding
        onClick={() => handleClickLinkButton(ROUTER_PATH.auth.signUp)}
      >
        회원가입
      </Button>
    </LinkButtonGroupStyle>
  );
};

/* Styles */
export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const LogoAreaStyle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  margin-top: 8.7rem;
  margin-bottom: 6.8rem;

  color: ${({ theme }) => theme.color.text.secondary};
  ${({ theme }) => theme.typography.body1.regular}
  letter-spacing: -0.048rem;

  svg {
    width: 18.5rem;
    color: ${({ theme }) => theme.color.primary};
  }

  ${({ theme }) => css`
    ${theme.media.small} {
      gap: 1.6rem;
      margin-top: 13.4rem;
      margin-bottom: 0;

      p {
        ${theme.typography.body3.regular}
        letter-spacing: -0.042rem;
      }

      svg {
        width: 16.3rem;
      }
    }
  `}
`;

export const SignInFormStyle = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;

  ${({ theme }) => css`
    ${theme.media.small} {
      gap: 1rem;
    }
  `}
`;

export const SignInFormFooterStyle = styled.footer`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;
export const LinkButtonGroupStyle = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;

  button {
    &:last-child {
      color: ${({ theme }) => theme.color.text.highEmphasis};
    }
  }
`;

export const SignInModalStyle = styled(Modal.Body)`
  ${({ theme }) => css`
    ${theme.media.notSmall} {
      width: 38rem;
    }
  `}
`;
